import {
  AlertTriangle,
  Check,
  Pencil,
  Send,
  X,
} from "lucide-react";
import {
  formatDisplayDate,
  formatEvaluationAmount,
} from "../../model";
import {
  buildCorrectionDraft,
} from "./approval-presentation";
import type { ApprovalRowContext } from "./approval-row-model";
import { procurementRequestCorrectionFieldNames, procurementRequestCorrectionFields, toggleCorrectionField } from "../../../../../shared/procurement-correction-fields";

export function ApprovalRevisionForm({ context }: { context: ApprovalRowContext ;}) {
  const {
    request,
    pendingDecision,
    decisionNote,
    setDecisionNote,
    decisionNoteManuallyEdited,
    setDecisionNoteManuallyEdited,
    decisionError,
    setDecisionError,
    decisionSubmitting,
    revisionFields,
    setRevisionFields,
    revisionFieldOptions,
    closeDecisionComposer,
    act,
    evaluation,
    displayedOffer,
  } = context;
  return (<>
    {pendingDecision?.requestId === request.id ? (
      pendingDecision.action ===
        "Uzmana geri gönder" ? (
        <div
          className="approval-revision-layer"
          onMouseDown={(event) => {
            if (
              event.target === event.currentTarget
            ) {
              closeDecisionComposer();
            }
          }}
        >
          <section
            className="approval-revision-composer"
            aria-labelledby={`revision-title-${request.id}`}
            role="dialog"
            aria-modal="true"
          >
            <header className="revision-composer-header">
              <div>
                <span className="section-kicker">
                  Düzeltme talimatı
                </span>
                <h3
                  id={`revision-title-${request.id}`}
                >
                  Hangi bilgilerin düzeltilmesini istiyorsunuz?
                </h3>
                <p>
                  İsterseniz ilgili konuları seçin;
                  dosyanın hangi koşulla yeniden
                  sunulacağını açıklayın.
                  Talep ve teklif bilgileri ayrı gönderilir; diğer gruptan seçim önceki konuları temizler.
                </p>
              </div>
              <button
                type="button"
                className="icon-button"
                aria-label="Düzeltme panelini kapat"
                onClick={closeDecisionComposer}
              >
                <X size={18} />
              </button>
            </header>
            <div className="revision-composer-body">
              <div className="revision-topic-group">
                <div>
                  <strong>
                    Düzeltilecek konular
                  </strong>
                  <span>İsteğe bağlı</span>
                </div>
                <div
                  className="revision-field-list"
                  aria-label="Düzeltilmesi istenen alanlar"
                >
                  {[...procurementRequestCorrectionFieldNames, ...revisionFieldOptions].map(
                    (field) => (
                      <button
                        key={field}
                        aria-label={field}
                        aria-pressed={revisionFields.includes(
                          field,
                        )}
                        className={
                          revisionFields.includes(
                            field,
                          )
                            ? "is-selected"
                            : ""
                        }
                        onClick={() =>
                          setRevisionFields((current) => toggleCorrectionField(current, field))
                        }
                      >
                        <span className="revision-field-check">
                          {revisionFields.includes(
                            field,
                          ) ? (
                            <Check size={13} />
                          ) : null}
                        </span>
                        {field}
                      </button>
                    ),
                  )}
                </div>
              </div>
              <label className="revision-note-field">
                <span className="revision-note-heading">
                  <span>Beklenen sonuç</span>
                  <button
                    type="button"
                    className="revision-draft-button"
                    disabled={
                      revisionFields.length === 0 ||
                      decisionNote.trim().length > 0
                    }
                    title={
                      decisionNote.trim().length > 0
                        ? "Mevcut metnin üzerine yazılmaz. Taslak için alanı boşaltın."
                        : undefined
                    }
                    onClick={() => {
                      if (!displayedOffer) return;
                      if (revisionFields.some((field) => procurementRequestCorrectionFields.has(field))) {
                        setDecisionNote(`${revisionFields.join(", ")} bilgileri kontrol edilip güncellensin; düzeltilmiş ihtiyaç yeniden sunulsun.`);
                        return;
                      }
                      const offerForDraft =
                        displayedOffer;
                      const instructions = [
                        ...new Set(
                          revisionFields.map(
                            (field) => {
                              if (
                                field ===
                                "Teklif fiyatı"
                              ) {
                                return `${offerForDraft.supplier} teklifindeki ${formatEvaluationAmount(offerForDraft.comparableTotal ?? offerForDraft.enteredTotal ?? offerForDraft.amount, evaluation.comparisonCurrency)} toplam tutar tedarikçiyle yeniden görüşülsün ve güncel fiyat teklifi dosyaya eklensin.`;
                              }
                              if (
                                field ===
                                "Teslimat süresi"
                              ) {
                                return `${offerForDraft.deliveryDays ?? "Kayıtlı olmayan"} günlük teslim süresi, ${formatDisplayDate(request.due)} ihtiyaç tarihi dikkate alınarak yeniden teyit edilsin ve güncel teslim taahhüdü kaydedilsin.`;
                              }
                              if (
                                field ===
                                "Ödeme vadesi"
                              ) {
                                return `${offerForDraft.paymentDays ?? "Kayıtlı olmayan"} günlük ödeme vadesi tedarikçiyle yeniden görüşülsün ve mutabık kalınan güncel vade teklife işlensin.`;
                              }
                              return "Tedarikçi ve teklif belgeleri eksiksiz, güncel ve teklif koşullarıyla tutarlı olacak şekilde dosyaya eklensin.";
                            },
                          ),
                        ),
                      ];
                      setDecisionNote(
                        `${instructions.join(" ")} Dosya, yapılan değişiklikler açıkça belirtilerek yeniden onaya sunulsun.`,
                      );
                    }}
                  >
                    <Pencil size={14} /> Seçime göre
                    taslak oluştur
                  </button>
                </span>
                <textarea
                  rows={5}
                  value={decisionNote}
                  aria-describedby={`revision-help-${request.id}`}
                  placeholder="Örneğin: Ödeme vadesi en az 30 gün olacak şekilde tedarikçiyle yeniden görüşülerek teklif güncellensin."
                  onChange={(event) =>
                    setDecisionNote(
                      event.target.value,
                    )
                  }
                />
                <small
                  id={`revision-help-${request.id}`}
                >
                  Ne değişeceğini ve dosyanın hangi
                  koşulla yeniden sunulacağını açıkça
                  yazın.
                </small>
              </label>
            </div>

            <footer className="revision-submit-bar">
              <div className="revision-submit-summary">
                <strong>
                  {revisionFields.some((field) => procurementRequestCorrectionFields.has(field))
                    ? "Talep sahibine düzeltme görevi"
                    : "Satın Alma Uzmanına düzeltme görevi"}
                </strong>
                <span>
                  {revisionFields.length > 0
                    ? `${revisionFields.length} düzeltme konusu seçildi`
                    : decisionNote.trim().length >= 10
                      ? "Genel düzeltme talebi gönderilecek"
                      : "Göndermek için açıklama yazın"}
                </span>
              </div>
              <div className="revision-submit-actions">
                <button
                  className="secondary-button"
                  onClick={closeDecisionComposer}
                >
                  Vazgeç
                </button>
                <button
                  className="warning-button"
                  disabled={
                    decisionNote.trim().length < 10
                  }
                  onClick={() =>
                    act(
                      request,
                      "Uzmana geri gönder",
                      decisionNote,
                    )
                  }
                >
                  <Send size={15} /> Düzeltme görevi
                  gönder
                </button>
              </div>
            </footer>
          </section>
        </div>
      ) : (
        <div
          className="approval-revision-layer"
          onMouseDown={(event) => {
            if (
              !decisionSubmitting &&
              event.target === event.currentTarget
            ) {
              closeDecisionComposer();
            }
          }}
        >
          <section
            className="approval-revision-composer gm-rejection-composer"
            aria-labelledby={`rejection-title-${request.id}`}
            role="dialog"
            aria-modal="true"
          >
            <header className="revision-composer-header">
              <div>
                <span className="section-kicker">
                  Genel Müdür ret kararı
                </span>
                <h3
                  id={`rejection-title-${request.id}`}
                >
                  Dosyayı düzeltme için iade edin
                </h3>
                <p>
                  Gerekçe Satın Alma Müdürüne
                  iletilir; uzman görevlendirmesini
                  Müdür yapar.
                </p>
              </div>
              <button
                type="button"
                className="icon-button"
                aria-label="Ret formunu kapat"
                disabled={decisionSubmitting}
                onClick={closeDecisionComposer}
              >
                <X size={18} />
              </button>
            </header>

            <div className="revision-composer-body">
              <div className="revision-topic-group">
                <div>
                  <strong>
                    Düzeltilecek konular
                  </strong>
                  <span>İsteğe bağlı</span>
                </div>
                <div
                  className="revision-field-list"
                  aria-label="Ret sonrası düzeltilmesi gereken alanlar"
                >
                  {revisionFieldOptions.map(
                    (field) => {
                      const selected =
                        revisionFields.includes(
                          field,
                        );
                      return (
                        <button
                          key={field}
                          type="button"
                          aria-label={field}
                          aria-pressed={selected}
                          className={
                            selected
                              ? "is-selected"
                              : ""
                          }
                          disabled={
                            decisionSubmitting
                          }
                          onClick={() => {
                            const nextFields =
                              selected
                                ? revisionFields.filter(
                                  (item) =>
                                    item !== field,
                                )
                                : [
                                  ...revisionFields,
                                  field,
                                ];
                            setRevisionFields(
                              nextFields,
                            );
                            setDecisionError("");
                            if (
                              !decisionNoteManuallyEdited
                            ) {
                              setDecisionNote(
                                buildCorrectionDraft(
                                  nextFields,
                                  displayedOffer,
                                  evaluation,
                                  request,
                                ),
                              );
                            }
                          }}
                        >
                          <span className="revision-field-check">
                            {selected ? (
                              <Check size={13} />
                            ) : null}
                          </span>
                          {field}
                        </button>
                      );
                    },
                  )}
                </div>
              </div>

              <label className="revision-note-field">
                <span className="revision-note-heading">
                  <span>Ret gerekçesi</span>
                  <small>
                    {decisionNote.trim().length}
                    /10+
                  </small>
                </span>
                <textarea
                  rows={6}
                  value={decisionNote}
                  disabled={decisionSubmitting}
                  aria-invalid={
                    decisionNote.trim().length > 0 &&
                    decisionNote.trim().length < 10
                  }
                  aria-describedby={`rejection-help-${request.id}`}
                  placeholder="Konu seçerek hazır bir metin oluşturun veya ret gerekçesini doğrudan yazın."
                  onChange={(event) => {
                    const value = event.target.value;
                    setDecisionNote(value);
                    setDecisionNoteManuallyEdited(
                      value.trim().length > 0,
                    );
                    setDecisionError("");
                  }}
                />
                <small
                  id={`rejection-help-${request.id}`}
                  className={
                    decisionNote.trim().length > 0 &&
                      decisionNote.trim().length < 10
                      ? "is-error"
                      : ""
                  }
                  aria-live="polite"
                >
                  {decisionNote.trim().length > 0 &&
                    decisionNote.trim().length < 10
                    ? `Göndermek için ${10 - decisionNote.trim().length} karakter daha yazın.`
                    : revisionFields.length > 0 &&
                      !decisionNoteManuallyEdited
                      ? "Hazır metni ihtiyacınıza göre düzenleyebilirsiniz."
                      : "Konu seçimi zorunlu değildir; en az 10 karakterlik açık bir gerekçe yazın."}
                </small>
              </label>

              {decisionError ? (
                <div
                  className="decision-inline-error"
                  role="alert"
                >
                  <AlertTriangle
                    size={18}
                    aria-hidden="true"
                  />
                  <div>
                    <strong>
                      Ret kararı gönderilemedi
                    </strong>
                    <span>{decisionError}</span>
                  </div>
                </div>
              ) : null}
            </div>

            <footer className="revision-submit-bar">
              <div className="revision-submit-summary">
                <strong>
                  Satın Alma Müdürüne iade
                </strong>
                <span>
                  {revisionFields.length > 0
                    ? `${revisionFields.length} düzeltme konusu seçildi`
                    : decisionNote.trim().length >= 10
                      ? "Genel ret gerekçesi gönderilecek"
                      : "Göndermek için gerekçeyi tamamlayın"}
                </span>
              </div>
              <div className="revision-submit-actions">
                <button
                  type="button"
                  className="secondary-button"
                  disabled={decisionSubmitting}
                  onClick={closeDecisionComposer}
                >
                  Vazgeç
                </button>
                <button
                  type="button"
                  className="reject-button"
                  disabled={
                    decisionSubmitting ||
                    decisionNote.trim().length < 10
                  }
                  onClick={() =>
                    act(
                      request,
                      "Reddedildi",
                      decisionNote,
                    )
                  }
                >
                  <Send size={15} />
                  {decisionSubmitting
                    ? "Gönderiliyor…"
                    : "Reddet ve Müdüre gönder"}
                </button>
              </div>
            </footer>
          </section>
        </div>
      )
    ) : null}
  </>);
}
